"use client";

import type { FormBlock as FormBlockType } from "@/payload-types";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import { RichText } from "@/components/RichText";
import { BlockContainer } from "@/components/shared/BlockContainer";
import { HONEYPOT_FIELD } from "@/lib/honeypot";
import { pushDataLayer } from "@/lib/gtm";

/**
 * Form Block Renderer
 * 
 * This component renders forms created with the Form Builder Plugin.
 * It handles form submission, validation, and redirects.
 */

interface FormRendererProps {
  block: FormBlockType;
  disableContainer?: boolean;
}

interface FormField {
  id?: string | null;
  name: string;
  label?: string | null;
  blockType: string;
  required?: boolean | null;
  placeholder?: string | null;
  rows?: number | null;
  options?: Array<{
    label: string;
    value: string;
  }> | null;
}

// Basic validation schema - will be enhanced based on form fields
const createValidationSchema = (fields: FormField[]) => {
  const schemaFields: Record<string, z.ZodType<unknown>> = {
    [HONEYPOT_FIELD]: z.string().optional(), // Honeypot for bot detection
  };

  fields?.forEach((field) => {
    if (field.blockType === "email") {
      let schema = z.string().email("Invalid email address");
      if (field.required) {
        schema = schema.min(1, "This field is required");
      }
      schemaFields[field.name] = schema;
    } else if (field.blockType === "text" || field.blockType === "textarea") {
      let schema = z.string();
      if (field.required) {
        schema = schema.min(1, "This field is required");
      }
      schemaFields[field.name] = schema;
    } else if (field.blockType === "number") {
      let schema = z.coerce.number();
      if (field.required) {
        schema = schema.min(1, "This field is required");
      }
      schemaFields[field.name] = schema;
    }
  });
  
  return z.object(schemaFields);
};

export function FormRenderer({ block, disableContainer = false }: FormRendererProps) {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const wrapContent = (content: ReactNode) => (
    disableContainer ? <>{content}</> : <BlockContainer>{content}</BlockContainer>
  );

  // Get form safely - must be before any hooks
  const form = (!block.form || typeof block.form === "string" || typeof block.form === "number") 
    ? null 
    : block.form;
    
  const validationSchema = createValidationSchema((form?.fields || []) as FormField[]);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  // Early return if no form is selected or if it's just an ID
  if (!form) {
    return wrapContent(
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded">
        <p>Please select a form to display.</p>
      </div>
    );
  }

  const onSubmit = async (data: Record<string, unknown>) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch("/api/form-submissions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          form: form.id,
          submissionData: data,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      setSubmitSuccess(true);
      reset();

      pushDataLayer("form_submit", {
        form_id: form.id,
        form_name: form.title,
      });

      // Handle redirect if enabled
      if (block.enableRedirect && block.redirectUrl) {
        setTimeout(() => {
          router.push(block.redirectUrl as string);
        }, 1500);
      }

      // Reset success message after delay if no redirect
      if (!block.enableRedirect) {
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitError("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render form fields
  const renderField = (field: FormField) => {
    const fieldError = errors[field.name];

    switch (field.blockType) {
      case "text":
        return (
          <div key={field.id} className="mb-4">
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              {...register(field.name)}
              type="text"
              id={field.name}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand focus:border-brand"
              placeholder={field.placeholder || undefined}
            />
            {fieldError && (
              <p className="mt-1 text-sm text-red-600">{fieldError.message as string}</p>
            )}
          </div>
        );

      case "email":
        return (
          <div key={field.id} className="mb-4">
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              {...register(field.name)}
              type="email"
              id={field.name}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand focus:border-brand"
              placeholder={field.placeholder || undefined}
            />
            {fieldError && (
              <p className="mt-1 text-sm text-red-600">{fieldError.message as string}</p>
            )}
          </div>
        );

      case "textarea":
        return (
          <div key={field.id} className="mb-4">
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              {...register(field.name)}
              id={field.name}
              rows={field.rows || 4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand focus:border-brand"
              placeholder={field.placeholder || undefined}
            />
            {fieldError && (
              <p className="mt-1 text-sm text-red-600">{fieldError.message as string}</p>
            )}
          </div>
        );

      case "select":
        return (
          <div key={field.id} className="mb-4">
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              {...register(field.name)}
              id={field.name}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand focus:border-brand"
            >
              <option value="">Select an option</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {fieldError && (
              <p className="mt-1 text-sm text-red-600">{fieldError.message as string}</p>
            )}
          </div>
        );

      case "checkbox":
        return (
          <div key={field.id} className="mb-4">
            <label className="flex items-start">
              <input
                {...register(field.name)}
                type="checkbox"
                className="mt-1 h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </span>
            </label>
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 ml-6">{fieldError.message as string}</p>
            )}
          </div>
        );

      case "number":
        return (
          <div key={field.id} className="mb-4">
            <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              {...register(field.name)}
              type="number"
              id={field.name}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand focus:border-brand"
              placeholder={field.placeholder || undefined}
            />
            {fieldError && (
              <p className="mt-1 text-sm text-red-600">{fieldError.message as string}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Helper to render confirmation message
  const renderConfirmationMessage = () => {
    if (!form.confirmationMessage) {
      return "Thank you! Your form has been submitted successfully.";
    }
    
    // Check if it's a rich text object (has root property)
    if (typeof form.confirmationMessage === 'object' && 'root' in form.confirmationMessage) {
      return <RichText content={form.confirmationMessage} />;
    }
    
    // Otherwise treat as string
    return form.confirmationMessage;
  };

  return wrapContent(
    <div>
      {form.title && <h2 className="text-2xl font-subheading mb-6">{form.title}</h2>}

      {submitSuccess && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded">
          {renderConfirmationMessage()}
        </div>
      )}

      {submitError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded">
          <p>{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Honeypot field - hidden from humans, bots fill it */}
        <input
          {...register(HONEYPOT_FIELD)}
          type="text"
          className="absolute -left-[9999px] opacity-0 h-0 w-0"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />

        {form.fields?.map((field) => {
          // Type guard to ensure field has required properties
          if (!field || typeof field !== 'object' || !('name' in field)) return null;
          return renderField(field as FormField);
        })}

        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 py-3 bg-brand text-white font-medium rounded-md hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Submitting..." : (form.submitButtonLabel || "Submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
